import { periodByOffset } from '../../utils/payPeriodGrid';
/**
 * Time Tracking Constants
 * Shared constants for TimeTrackingPage and related components
 */

import {
  AcademicCapIcon,
  UserGroupIcon,
  ArrowsRightLeftIcon,
  DocumentTextIcon,
  PhoneIcon,
  ClipboardDocumentListIcon,
  TruckIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  EllipsisHorizontalIcon
} from '@heroicons/react/24/outline';

// Heroicon component map for activity types
export const ACTIVITY_ICON_MAP = {
  supervision_given: AcademicCapIcon,
  supervision_received: AcademicCapIcon,
  staff_meeting: UserGroupIcon,
  training: AcademicCapIcon,
  care_coordination: ArrowsRightLeftIcon,
  documentation: DocumentTextIcon,
  phone_call: PhoneIcon,
  administrative: ClipboardDocumentListIcon,
  travel: TruckIcon,
  consultation: ChatBubbleLeftRightIcon,
  case_review: ChartBarIcon,
  team_meeting: UserGroupIcon,
  crisis_response: ExclamationTriangleIcon,
  other: EllipsisHorizontalIcon
};

// FALLBACK ONLY. The server owns this list — see GET /time-tracking/activity-types.
// Editing it here does not change what the backend accepts, and divergence
// between this list and the mobile app's copy is what caused entries to
// mis-bucket depending on where they were logged.
// Activity type labels
export const ACTIVITY_TYPES = {
  supervision_given: { label: 'Supervision Given', category: 'clinical' },
  supervision_received: { label: 'Supervision Received', category: 'clinical' },
  staff_meeting: { label: 'Staff Meeting', category: 'administrative' },
  training: { label: 'Training/CEU', category: 'professional_development' },
  care_coordination: { label: 'Care Coordination', category: 'clinical' },
  documentation: { label: 'Documentation', category: 'clinical' },
  phone_call: { label: 'Phone Call (Non-Billable)', category: 'clinical' },
  administrative: { label: 'Administrative Tasks', category: 'administrative' },
  travel: { label: 'Travel', category: 'travel' },
  consultation: { label: 'Consultation', category: 'clinical' },
  case_review: { label: 'Case Review', category: 'clinical' },
  team_meeting: { label: 'Team Meeting', category: 'administrative' },
  crisis_response: { label: 'Crisis Response (Non-Billable)', category: 'clinical' },
  other: { label: 'Other', category: 'other' }
};

// Category colors - consistent with design tokens
export const CATEGORY_COLORS = {
  clinical: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300',
  administrative: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300',
  professional_development: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300',
  travel: 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300',
  other: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
};

// Entry type styling - aligned with design tokens status colors
export const ENTRY_TYPE_STYLES = {
  billable: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-700', text: 'text-emerald-700 dark:text-emerald-300', badge: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300' },
  travel: { bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-700', text: 'text-orange-700 dark:text-orange-300', badge: 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300' },
  non_billable: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-700', text: 'text-blue-700 dark:text-blue-300', badge: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300' },
  driver_shift: { bg: 'bg-teal-50 dark:bg-teal-900/20', border: 'border-teal-200 dark:border-teal-700', text: 'text-teal-700 dark:text-teal-300', badge: 'bg-teal-100 dark:bg-teal-900/50 text-teal-800 dark:text-teal-300' }
};

// Day labels for weekly view
export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Pay periods follow the organization's own pay period grid (Settings > Pay
// period, utils/payPeriodGrid): every two weeks or twice a month. No grid, no
// periods; callers show "not set up" instead of guessing. usePayPeriods() is
// the hook pages use.
export const PERIODS_TO_SHOW_PAST = 26;
export const PERIODS_TO_SHOW_FUTURE = 26;

// Get today's date in YYYY-MM-DD format
export const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Get first day of current month
export const getMonthStart = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
};

const localDayKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Periods around today on the organization's grid, oldest first: [{ start, end }]. */
export const generatePayPeriods = (grid, today = new Date()) => {
  if (!grid?.configured) return [];
  const todayKey = localDayKey(today);
  const periods = [];
  for (let i = -PERIODS_TO_SHOW_PAST; i <= PERIODS_TO_SHOW_FUTURE; i++) {
    const p = periodByOffset(todayKey, i, grid);
    periods.push({ start: p.start, end: p.end });
  }
  return periods;
};

/** Index of the period holding today in `periods` (from generatePayPeriods). */
export const findCurrentPayPeriodIndex = (periods = [], today = new Date()) => {
  const todayKey = localDayKey(today);
  const index = periods.findIndex((p) => p.start <= todayKey && todayKey <= p.end);
  return index === -1 ? Math.min(PERIODS_TO_SHOW_PAST, Math.max(periods.length - 1, 0)) : index;
};

/** The period at `index` in `periods`, labelled relative to today; null when out of range. */
export const getPayPeriod = (index, periods = [], today = new Date()) => {
  if (index < 0 || index >= periods.length) {
    return null;
  }

  const period = periods[index];
  const currentIndex = findCurrentPayPeriodIndex(periods, today);
  const start = new Date(period.start + 'T00:00:00');
  const end = new Date(period.end + 'T23:59:59');

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  };

  let label;
  if (index === currentIndex) {
    label = 'Current Pay Period';
  } else if (index === currentIndex - 1) {
    label = 'Previous Pay Period';
  } else if (index === currentIndex + 1) {
    label = 'Next Pay Period';
  } else {
    label = `${formatDate(start)} - ${formatDate(end)}`;
  }

  return {
    start: period.start,
    end: period.end,
    label,
    dateRange: `${formatDate(start)} - ${formatDate(end)}`
  };
};

// Format duration
export const formatDuration = (minutes) => {
  if (!minutes || minutes === 0) return '0m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

// Format hours
export const formatHours = (hours) => {
  if (!hours) return '0';
  return hours.toFixed(1);
};

// Format date
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';

  try {
    const dateToFormat = dateString.includes('T')
      ? new Date(dateString)
      : new Date(dateString + 'T00:00:00');

    if (isNaN(dateToFormat.getTime())) return 'Invalid Date';

    return dateToFormat.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  } catch (err) {
    console.error('Error formatting date:', dateString, err);
    return 'Invalid Date';
  }
};
